import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Box } from "@mui/material";
import { Document, Page } from "react-pdf";

import { handleMouseDown } from "../../../../../uploaderComponents/helpers/mouseEvents.js/mouseDown";
import { handleMouseMove } from "../../../../../uploaderComponents/helpers/mouseEvents.js/mouseMove";
import { handleMouseUp } from "../../../../../uploaderComponents/helpers/mouseEvents.js/mouseUp";

import SelectionRectOverlay from "./overlays/SelectionRectOverlay";
import FieldToolPreviewOverlay from "./overlays/FieldToolPreviewOverlay";
import FormFieldOverlayLayer from "./overlays/FormFieldOverlayLayer";
import PdfTextOverlayLayer from "./overlays/PdfTextOverlayLayer";
import { canvasToPdfPoints } from "../../../../../../utils/coordinateUtils";
import { getEffectivePageRotation } from "../utils/documentOrientation";
import {
  copySelectedFieldGeometry,
  duplicateCopiedFields,
} from "../utils/fieldClipboard";

const createExtraFieldName = () => {
  if (typeof crypto !== "undefined" && crypto?.randomUUID) {
    return `extra_field_${crypto.randomUUID()}`;
  }

  return `extra_field_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const isEditableTarget = (target) => {
  if (!target || typeof target.closest !== "function") {
    return false;
  }

  return Boolean(
    target.closest(
      'input, textarea, select, [contenteditable="true"], [contenteditable=""], [role="textbox"]',
    ),
  );
};

const hasNativeTextSelection = () => {
  if (typeof window === "undefined" || !window.getSelection) return false;
  const selection = window.getSelection();
  return Boolean(selection && selection.toString().length > 0);
};

const StablePdfPage = React.memo(
  ({
    file,
    pageNumber,
    scale,
    displayRotation,
    canvasRef,
    pdfDocumentRef,
    onDocumentLoadSuccessRef,
    onPageLoadSuccessRef,
    buildPdfTextItemsForPageRef,
  }) => {
    return (
      <Document
        key={file ? "loaded" : "loading"}
        file={file}
        onLoadSuccess={(pdf) => onDocumentLoadSuccessRef.current?.(pdf)}
        onLoadError={(e) => console.error("Document load error:", e)}
        ref={pdfDocumentRef}
      >
        <Page
          pageNumber={pageNumber}
          canvasRef={canvasRef}
          scale={scale}
          rotate={displayRotation}
          renderTextLayer
          renderAnnotationLayer
          className="custom-react-pdf"
          onLoadSuccess={(page) => onPageLoadSuccessRef.current?.(page)}
          onRenderTextLayerSuccess={() =>
            buildPdfTextItemsForPageRef.current?.()
          }
          onLoadError={(error) => console.error("Page load error:", error)}
        />
      </Document>
    );
  },
  (prev, next) =>
    prev.file === next.file &&
    prev.pageNumber === next.pageNumber &&
    prev.scale === next.scale &&
    prev.displayRotation === next.displayRotation,
);

StablePdfPage.displayName = "StablePdfPage";

const PDFCanvas = ({
  base64File,
  pdfInput,
  pdfScrollContainerRef,
  pdfContainerRef,
  pdfDocumentRef,
  currentProcessingFile,
  setCurrentProcessingFile,
  pageNumber,
  scale,
  displayRotation,
  selectToolActive,
  typingToolActive,
  highlightFields,
  textOverlayDisplayMode,
  focusedField,
  setFocusedField,
  currCursor,
  unassignedFieldNames,
  fixedInputs,
  inputRef,
  childInputRef,
  resizing,
  setResizing,
  moving,
  setMoving,
  isDrawing,
  setIsDrawing,
  isSelecting,
  setIsSelecting,
  rect,
  setRect,
  selectionRect,
  setSelectionRect,
  freeSelectStartPoint,
  setFreeSelectStartPoint,
  startPoint,
  setStartPoint,
  initialMousePosition,
  setInitialMousePosition,
  initialInputSize,
  setInitialInputSize,
  inputPosition,
  setInputPosition,
  resizeDirection,
  setResizeDirection,
  setCurrCursor,
  onDocumentLoadSuccess,
  onPageLoadSuccess,
  handleFieldChange,
  undoRedoStats,
  activeEditorTool,
  handleEditorToolSelection,
  textSelectionMode,
  formFields,
  onNavigateToTaggedField,
}) => {
  const scaleFactor =
    currentProcessingFile?.document?.tempFields?.currentDocument?.scaleFactor ||
    1;
  const pageProxyRef = useRef(null);
  const extractedTextLayerKeyRef = useRef(null);
  const canvasRef = useRef(null);
  const copiedFieldGeometriesRef = useRef([]);

  const [canvasSize, setCanvasSize] = useState({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;

      if (width === 0 || height === 0) return;

      setCanvasSize((prev) =>
        prev.width === width && prev.height === height
          ? prev
          : { width, height },
      );
    });

    ro.observe(canvas);
    return () => ro.disconnect();
  }, [scale, pageNumber]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = ""; // Chrome requires returnValue to be set

      return ""; // Some browsers require a return value
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const guardPushed = useRef(false);

  useEffect(() => {
    if (!guardPushed.current) {
      window.history.pushState(null, "", window.location.href);
      guardPushed.current = true;
    }

    const handlePopState = () => {
      const leave = window.confirm("Are you sure you want to leave this page?");
      if (leave) {
        window.removeEventListener("popstate", handlePopState);
        window.history.back();
      } else {
        window.history.pushState(null, "", window.location.href);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const onPageLoadSuccessRef = useRef((page) => {
    pageProxyRef.current = page;
    extractedTextLayerKeyRef.current = null;
    onPageLoadSuccess(page);
  });

  useLayoutEffect(() => {
    onPageLoadSuccessRef.current = (page) => {
      pageProxyRef.current = page;
      extractedTextLayerKeyRef.current = null;
      onPageLoadSuccess(page);
    };
  });

  useEffect(() => {
    if (!pageProxyRef.current) return;

    extractedTextLayerKeyRef.current = null;
    onPageLoadSuccessRef.current?.(pageProxyRef.current);
  }, [displayRotation]);

  const onDocumentLoadSuccessRef = useRef(onDocumentLoadSuccess);
  useLayoutEffect(() => {
    onDocumentLoadSuccessRef.current = onDocumentLoadSuccess;
  });

  const buildPdfTextItemsForPage = useCallback(() => {
    const textLayer = pdfContainerRef?.current?.querySelector(
      ".react-pdf__Page__textContent",
    );
    const pageNode =
      pdfContainerRef?.current?.querySelector(".react-pdf__Page");
    const page = pageProxyRef.current;
    if (!textLayer || !pageNode || !page?.getViewport) return;

    const viewport = page.getViewport({
      scale,
      rotation: getEffectivePageRotation(page.rotate, displayRotation),
    });
    const extractionKey = `${pageNumber}_${scale}_${Math.round(viewport.width)}_${Math.round(viewport.height)}`;

    if (extractedTextLayerKeyRef.current === extractionKey) {
      return;
    }

    const pageRect = pageNode.getBoundingClientRect();
    const spans = Array.from(textLayer.querySelectorAll("span"));
    const items = [];

    spans.forEach((span, spanIndex) => {
      const text = (span.textContent || "").replace(/\s+/g, " ").trim();
      if (!text) return;

      const spanRect = span.getBoundingClientRect();
      const relativeLeft = spanRect.left - pageRect.left;
      const relativeTop = spanRect.top - pageRect.top;
      const relativeWidth = spanRect.width;
      const relativeHeight = spanRect.height;

      const runRect = canvasToPdfPoints(
        relativeLeft,
        relativeTop,
        relativeWidth,
        relativeHeight,
        viewport,
      );

      items.push({
        id: `run_${pageNumber}_${spanIndex}`,
        page: pageNumber,
        kind: "run",
        text,
        rect: {
          X: runRect.x,
          Y: runRect.y,
          W: runRect.width,
          H: runRect.height,
        },
      });

      const words = text.split(/\s+/).filter(Boolean);
      const totalCharacters = words.join("").length || text.length || 1;
      let offsetChars = 0;

      words.forEach((word, wordIndex) => {
        const wordChars = word.length || 1;
        const wordWidthPx = (relativeWidth * wordChars) / totalCharacters;
        const wordLeftPx =
          relativeLeft + (relativeWidth * offsetChars) / totalCharacters;
        offsetChars += wordChars;

        const wordRect = canvasToPdfPoints(
          wordLeftPx,
          relativeTop,
          wordWidthPx,
          relativeHeight,
          viewport,
        );

        items.push({
          id: `word_${pageNumber}_${spanIndex}_${wordIndex}`,
          page: pageNumber,
          kind: "word",
          text: word,
          rect: {
            X: wordRect.x,
            Y: wordRect.y,
            W: wordRect.width,
            H: wordRect.height,
          },
        });
      });
    });

    setCurrentProcessingFile((prev) => ({
      ...prev,
      document: {
        ...prev.document,
        tempFields: {
          ...prev.document.tempFields,
          pdfTextItemsByPage: {
            ...(prev.document.tempFields.pdfTextItemsByPage || {}),
            [pageNumber]: items,
          },
        },
      },
    }));

    extractedTextLayerKeyRef.current = extractionKey;
  }, [
    displayRotation,
    pageNumber,
    pdfContainerRef,
    scale,
    setCurrentProcessingFile,
  ]);

  const buildPdfTextItemsForPageRef = useRef(buildPdfTextItemsForPage);
  useLayoutEffect(() => {
    buildPdfTextItemsForPageRef.current = buildPdfTextItemsForPage;
  });

  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const ctrl = isMac ? e.metaKey : e.ctrlKey;
      const isTypingInEditable = isEditableTarget(e.target);
      const isEscToEditTool = [
        "checkbox",
        "textField",
        "insertText",
        "rectangle",
        "line",
      ].includes(activeEditorTool);
      const tempFields = currentProcessingFile?.document?.tempFields;
      const selectedFields = copySelectedFieldGeometry(tempFields, pageNumber);
      const key = e.key?.toLowerCase();

      if (
        ctrl &&
        key === "c" &&
        activeEditorTool === "edit" &&
        selectedFields.length > 0 &&
        !hasNativeTextSelection()
      ) {
        e.preventDefault();
        copiedFieldGeometriesRef.current = selectedFields;

        return;
      }

      if (
        ctrl &&
        key === "v" &&
        activeEditorTool === "edit" &&
        copiedFieldGeometriesRef.current.length > 0 &&
        !hasNativeTextSelection()
      ) {
        e.preventDefault();

        setCurrentProcessingFile((prevFile) => {
          const prevTempFields = prevFile?.document?.tempFields || {};
          const newFields = duplicateCopiedFields({
            copiedFields: copiedFieldGeometriesRef.current,
            existingFields: prevTempFields.formFields || [],
            fallbackPage: pageNumber,
            createName: createExtraFieldName,
          });

          if (newFields.length === 0) return prevFile;

          return {
            ...prevFile,
            document: {
              ...prevFile.document,
              tempFields: {
                ...prevTempFields,
                formFields: [
                  ...(prevTempFields.formFields || []),
                  ...newFields,
                ],
                selectedFormField: newFields[0],
                selectedExtraFields: newFields,
                selectedPdfTextItem: null,
                selectedExtraPdfTextItems: [],
              },
            },
          };
        });

        return;
      }

      if (e.key === "Escape" && isEscToEditTool && !isTypingInEditable) {
        e.preventDefault();
        handleEditorToolSelection?.("edit");
      } else if (isTypingInEditable) {
        return;
      } else if (ctrl && key === "z" && !e.shiftKey) {
        e.preventDefault();
        undoRedoStats?.undo();
      } else if (ctrl && (key === "y" || (key === "z" && e.shiftKey))) {
        e.preventDefault();
        undoRedoStats?.redo();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    activeEditorTool,
    currentProcessingFile,
    handleEditorToolSelection,
    pageNumber,
    setCurrentProcessingFile,
    undoRedoStats,
  ]);

  if (!(base64File || pdfInput)) return null;

  // console.log(canvasSize)

  return (
    <Box
      ref={pdfScrollContainerRef}
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        alignItems: "flex-start",
        overflow: "auto",
        p: 2,
        height: "calc(100vh - 256px)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          margin: "auto",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(228, 228, 228, 0.5)",
          borderRadius: 2,
          p: 2,
        }}
      >
        <Box
          ref={pdfContainerRef}
          sx={{
            position: "relative",
            display: "inline-block",
            border: "2px solid #d3d3d3",
            borderRadius: "18px",
            boxShadow: "3px 4px 12px rgba(0, 0, 0, 0.3)",
            backgroundColor: "#fff",
            cursor: currCursor || "default",
            userSelect: "none",
            width: canvasSize.width ?? "auto",
            height: canvasSize.height ?? "auto",
          }}
          onMouseDown={(e) => {
            const isToolActive = Object.values(
              currentProcessingFile?.document?.tempFields?.formFieldTools || {},
            ).some((field) => field.active);

            if (isToolActive) {
              handleMouseDown(
                e,
                currentProcessingFile,
                setCurrentProcessingFile,
                null,
                null,
                "fieldTools",
                "crosshair",
                pdfContainerRef,
                setStartPoint,
                setRect,
                setIsDrawing,
                setCurrCursor,
                setFreeSelectStartPoint,
                setMoving,
                setIsSelecting,
                setInitialMousePosition,
                setInitialInputSize,
                setInputPosition,
                setSelectionRect,
                setResizing,
                setResizeDirection,
                undoRedoStats?.pushHistory,
                pageNumber,
              );
            } else if (selectToolActive) {
              handleMouseDown(
                e,
                currentProcessingFile,
                setCurrentProcessingFile,
                null,
                null,
                "freeSelect",
                "default",
                pdfContainerRef,
                setStartPoint,
                setRect,
                setIsDrawing,
                setCurrCursor,
                setFreeSelectStartPoint,
                setMoving,
                setIsSelecting,
                setInitialMousePosition,
                setInitialInputSize,
                setInputPosition,
                setSelectionRect,
                setResizing,
                setResizeDirection,
                undoRedoStats?.pushHistory,
                pageNumber,
              );
            }
          }}
          onMouseMove={(e) => {
            const isToolActive = Object.values(
              currentProcessingFile?.document?.tempFields?.formFieldTools || {},
            ).some((field) => field.active);

            if (isToolActive || selectToolActive) {
              handleMouseMove(
                e,
                currentProcessingFile,
                setCurrentProcessingFile,
                pdfContainerRef,
                resizing,
                initialMousePosition,
                initialInputSize,
                inputPosition,
                resizeDirection,
                moving,
                isDrawing,
                startPoint,
                setRect,
                isSelecting,
                freeSelectStartPoint,
                setSelectionRect,
                pageNumber,
              );
            }
          }}
          onMouseUp={() => {
            const isToolActive = Object.values(
              currentProcessingFile?.document?.tempFields?.formFieldTools || {},
            ).some((field) => field.active);

            if (isToolActive || selectToolActive) {
              handleMouseUp(
                currentProcessingFile,
                setCurrentProcessingFile,
                resizing,
                setResizing,
                setCurrCursor,
                moving,
                setMoving,
                isDrawing,
                rect,
                setIsDrawing,
                setRect,
                fixedInputs,
                isSelecting,
                setIsSelecting,
                setSelectionRect,
                setFreeSelectStartPoint,
              );
            }
          }}
        >
          {/* StablePdfPage only re-renders when file/pageNumber/scale change,
              never when currentProcessingFile changes due to field interactions.
              This prevents pdf.js from aborting its async TextLayer render task. */}
          <StablePdfPage
            file={base64File || pdfInput}
            pageNumber={pageNumber}
            scale={scale}
            displayRotation={displayRotation}
            canvasRef={canvasRef}
            pdfDocumentRef={pdfDocumentRef}
            onDocumentLoadSuccessRef={onDocumentLoadSuccessRef}
            onPageLoadSuccessRef={onPageLoadSuccessRef}
            buildPdfTextItemsForPageRef={buildPdfTextItemsForPageRef}
          />

          {isDrawing && (
            <SelectionRectOverlay
              rect={{
                left: rect?.x * scaleFactor,
                top: rect?.y * scaleFactor,
                width: rect?.width * scaleFactor,
                height: rect?.height * scaleFactor,
              }}
              activeEditorTool={activeEditorTool}
            />
          )}

          {isSelecting && selectionRect && (
            <SelectionRectOverlay
              rect={{
                left: selectionRect.x * scaleFactor,
                top: selectionRect.y * scaleFactor,
                width: selectionRect.width * scaleFactor,
                height: selectionRect.height * scaleFactor,
              }}
              activeEditorTool={activeEditorTool}
            />
          )}

          <FieldToolPreviewOverlay
            fields={
              currentProcessingFile?.document?.tempFields?.formFieldTools
                ?.textField?.values || []
            }
          />

          <PdfTextOverlayLayer
            currentProcessingFile={currentProcessingFile}
            pageNumber={pageNumber}
            selectToolActive={selectToolActive}
            highlightFields={highlightFields}
            setCurrentProcessingFile={setCurrentProcessingFile}
            textSelectionMode={textSelectionMode}
          />

          <FormFieldOverlayLayer
            currentProcessingFile={currentProcessingFile}
            pageNumber={pageNumber}
            scale={scale}
            selectToolActive={selectToolActive}
            typingToolActive={typingToolActive}
            highlightFields={highlightFields}
            textOverlayDisplayMode={textOverlayDisplayMode}
            focusedField={focusedField}
            setFocusedField={setFocusedField}
            currCursor={currCursor}
            unassignedFieldNames={unassignedFieldNames}
            fixedInputs={fixedInputs}
            inputRef={inputRef}
            childInputRef={childInputRef}
            pdfContainerRef={pdfContainerRef}
            setCurrentProcessingFile={setCurrentProcessingFile}
            setStartPoint={setStartPoint}
            setRect={setRect}
            setIsDrawing={setIsDrawing}
            setCurrCursor={setCurrCursor}
            setFreeSelectStartPoint={setFreeSelectStartPoint}
            setMoving={setMoving}
            setIsSelecting={setIsSelecting}
            setInitialMousePosition={setInitialMousePosition}
            setInitialInputSize={setInitialInputSize}
            setInputPosition={setInputPosition}
            setSelectionRect={setSelectionRect}
            setResizing={setResizing}
            setResizeDirection={setResizeDirection}
            handleMouseDown={handleMouseDown}
            handleFieldChange={handleFieldChange}
            undoRedoStats={undoRedoStats}
            activeEditorTool={activeEditorTool}
            formFields={formFields}
            onNavigateToTaggedField={onNavigateToTaggedField}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PDFCanvas;
